import styles from './template.css';
import template from './template';
import {AoflElement, customElement, property} from '@aofl/element';
import {isMobile} from '@aoflmkt/is-mobile';
import {environment, eventNames} from '../../../../../modules/constants-enumerate';
import {marketingTrackerQueue} from '../../../../../modules/marketing-tracker-queue';
import {ModalService} from '../../../../../modules/modal-service';
import {layoutSdo} from '../../../../../modules/sdo-layout';
import {academyVideoSdo} from './modules/sdo';
import {SentryService} from '../../../../../modules/sentry-service';

/**
 * @extends {AoflElement}
 */
@customElement('academy-video')
class AcademyVideo extends AoflElement {
  /**
   * Creates an instance of AcademyVideo.
   */
  constructor() {
    super();
    this.addEventListener('click', (e) => e.stopPropagation()); // prevents replaying the video
  }

  /**
   * @readonly
   */
  static is = 'academy-video';

  @property({type: Boolean, attribute: false})
  iosInApp = (environment.IN_APP && isMobile.apple.device);

  @property({type: Boolean, attribute: false})
  androidInApp = (environment.IN_APP && isMobile.android.device);

  @property({type: String, attribute: false, store: academyVideoSdo, mapState: 'videoSrc'})
  videoSrc = '/assets/routes/home/WIAA-mb-v5.mp4';

  /**
   *
   */
  isMacOS() {
    const ua = window.navigator.userAgent;
    const macOS = ua.indexOf('Mac OS');

    if (macOS > 0) return true;
  }

  /**
   *
   */
  async play() {
    this.showOverlay();
    const macOS = this.isMacOS();
    const landscapeView = window.innerWidth > 768;
    if (landscapeView) {
      academyVideoSdo.videoSrc = '/assets/routes/home/WIAA-mb-v6.mp4';
      this.videoSrc = '/assets/routes/home/WIAA-mb-v6.mp4';
    }
    if (isMobile.any && !macOS && !this.iosInApp) {
      this.requestFullscreen();
    }
    this.video.addEventListener('webkitendfullscreen', () => this.handleClose());
    try {
      await this.video.load();
      await this.video.play();
      layoutSdo.scrollFreeze(true);
    } catch (err) {
      SentryService.captureException(err);
    }
  }

  /**
   *
   */
  async handleClose() {
    try {
      this.video.pause();
      await this.logVideoCompletionEvent(this.video.currentTime);
      this.video.currentTime = 0;
      this.hideOverlay();
      this.forceExitFullscreen();
      this.video.removeEventListener('webkitendfullscreen', this.handleClose);
      ModalService.show('prospectRegModal');
    } catch (err) {
      SentryService.captureException(err);
    }
  }

  /**
   *
   */
  showOverlay() {
    layoutSdo.overlay = true;
    this.classList.add('active');
  }

  /**
   *
   */
  hideOverlay() {
    layoutSdo.overlay = true;
    layoutSdo.scrollFreeze(false);
    this.classList.remove('active');
  }

  /**
   * Stop the video to prevent mobile from viewing the video outside of fullscreen
   */
  handleFullscreenchange() {
    const isFullScreen = this.detectFullscreen();
    if (!isFullScreen) {
      this.handleClose();
    }
  }
  /**
   *
   */
  requestFullscreen() {
    const fns = [
      'requestFullscreen', 'mozRequestFullScreen',
      'webkitRequestFullscreen', 'msRequestFullscreen'
    ];
    for (let i = 0; i < fns.length; i++) {
      if (typeof this.video[fns[i]] === 'function') {
        this.video[fns[i]]();
        if (this.video.webkitRequestFullscreen) {
          window.screen.orientation.lock('landscape-primary');
        }
        break;
      }
    }
  }
  /**
   *
   */
  detectFullscreen() {
    const fullscreenElements = ['fullscreenElement', 'mozFullScreenElement', 'webkitFullscreenElement', 'msFullscreenElement'];
    for (let i = 0; i < fullscreenElements.length; i++) {
      if (document[fullscreenElements[i]]) {
        return true;
      }
    }
    return false;
  }
  /**
   *
   */
  forceExitFullscreen() {
    if (isMobile.apple.device) {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (this.video.webkitExitFullScreen) {
        this.video.webkitExitFullScreen();
      }
    }
    const isFullScreen = this.detectFullscreen();
    if (!isFullScreen) {
      return;
    }
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen();
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    } else if (this.video.webkitExitFullScreen) {
      this.video.webkitExitFullScreen();
    }
  }

  /**
   *
   * @param {Object} res API res with payload of code_info and code
   */
  async logVideoCompletionEvent(videoCurrentTime) {
    let videoCompletion = Number(((videoCurrentTime / this.video.duration) * 100).toFixed(2));
    videoCompletion = Number.isNaN(videoCompletion) || !isFinite(videoCompletion) ? 0 : videoCompletion;
    try {
      await marketingTrackerQueue.enqueueEvent({
        event_type: eventNames.VIDEO_PROGRESS,
        payload: {
          video_name: 'WIAA',
          video_completion: videoCompletion
        }
      });
    } catch (err) {
      SentryService.captureException(err);
    }
  }

  /**
   *
   */
  firstUpdated() {
    super.firstUpdated();
    this.video = this.video || this.shadowRoot.getElementById('academy-video');
    if (this.androidInApp) {
      this.video.muted = true;
    }
    this.video.onwebkitfullscreenchange = () => {
      this.handleFullscreenchange();
    };
  }

  /**
   * @return {Object}
   */
  render() {
    return super.render(template, [styles]);
  }
}

export default AcademyVideo;
